import { API_BASE_URL } from '@/config';

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * The current session token, if any.
 *
 * Module-level rather than a hook argument: every service module already calls
 * `request` directly, and threading a token through each of them would put auth
 * plumbing into functions that have nothing to do with it. AuthContext owns the
 * value and pushes it here whenever the session changes.
 */
let authToken = null;

/** Called by AuthContext on sign-in, sign-out, and on restoring a saved session. */
export function setAuthToken(token) {
  authToken = token ?? null;
}

/**
 * Notified when the server rejects the token we sent.
 *
 * Sessions are restored from storage and outlive the tab, so a token can be
 * expired long before anything asks the server about it. Rather than checking on
 * every page load, the first request that comes back 401 is what ends the
 * session — AuthContext registers the handler that clears it.
 */
let onUnauthorized = null;

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

/**
 * Thin fetch wrapper. Every service module goes through this so that swapping
 * the mock data for the real FastAPI backend is a one-line change per service.
 */
export async function request(path, { method = 'GET', body, signal, headers, anonymous } = {}) {
  // `anonymous` marks a request that carries no session and whose 401 means
  // "those credentials are wrong", not "your session died" — signing the user
  // out because a *login* was rejected would be nonsense.
  const sendToken = Boolean(authToken) && !anonymous;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    signal,
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : null),
      ...(sendToken ? { Authorization: `Bearer ${authToken}` } : null),
      ...headers,
    },
    ...(body ? { body: JSON.stringify(body) } : null),
  });

  const payload = res.status === 204 ? null : await res.json().catch(() => null);

  if (res.status === 401 && sendToken) {
    onUnauthorized?.();
  }

  if (!res.ok) {
    // FastAPI puts validation and HTTPException messages under `detail`.
    // Refusals that carry a code (an earlier version already owned, a coupon that
    // doesn't apply) send `detail` as {message, error_code}; the code stays on
    // `payload` for callers that branch on it.
    const message =
      (typeof payload?.detail === 'string' && payload.detail) ||
      (typeof payload?.detail?.message === 'string' && payload.detail.message) ||
      payload?.message ||
      `Request failed with ${res.status}`;
    throw new ApiError(message, res.status, payload);
  }

  return payload;
}

/** Lets the mock services feel async so components handle loading states from day one. */
export const delay = (ms = 180) => new Promise((resolve) => setTimeout(resolve, ms));
